import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useClinicContext } from './useClinicContext';
import useAppToast from '@/hooks/useAppToast';
import { getApiErrorMessage, DEFAULT_FETCH_ERROR_MESSAGE } from '@/utils/apiError';
import {
  changePriceR2VisitClinicInvoice,
  printR2VisitClinicInvoiceListPdf,
  printResetR2VisitClinicInvoice,
  type R2VisitClinicInvoiceListData,
} from '@/services/main/visitClinicInvoice';

/**
 * SCR-182 §1.12 印刷「済」 reset for R2.
 *
 * Same contract as useContinuationPrintReset, but it hits the endpoint that writes
 * `r_visits`. BD §1.12: the screen must NOT recalculate on reset — the amounts stay
 * until the user presses 検索 — so only the row's 「済」 is cleared in the cached list
 * instead of invalidating (which refetched and recomputed 領収金額 at once).
 */
export function useR2VisitClinicInvoicePrintReset() {
  const { tenant, clinicNumber } = useClinicContext();
  const queryClient = useQueryClient();
  const toast = useAppToast();

  return useMutation({
    mutationFn: (visitId: number) =>
      printResetR2VisitClinicInvoice(tenant ?? '', clinicNumber ?? '', visitId),
    onSuccess: (_data, visitId) => {
      queryClient.setQueriesData<R2VisitClinicInvoiceListData>(
        { queryKey: ['visitClinicInvoice', 'r2-list'] },
        old =>
          old && {
            ...old,
            list: old.list.map(row =>
              row.id === visitId ? { ...row, print_invoice_mark: '' } : row
            ),
          }
      );
      toast.success('プリントチェックをリセットしました。');
    },
    onError: err => {
      toast.error(getApiErrorMessage(err, DEFAULT_FETCH_ERROR_MESSAGE));
    },
  });
}

/**
 * SCR-182 §1.13 領収金額 (自由診療 fee) edit for R2 — the `r_visits` counterpart of
 * useContinuationChangePrice.
 */
export function useR2VisitClinicInvoiceChangePrice() {
  const { tenant, clinicNumber } = useClinicContext();
  const queryClient = useQueryClient();
  const toast = useAppToast();

  return useMutation({
    mutationFn: ({ visitId, freeMedicalFee }: { visitId: number; freeMedicalFee: number }) =>
      changePriceR2VisitClinicInvoice(tenant ?? '', clinicNumber ?? '', visitId, freeMedicalFee),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['visitClinicInvoice'] });
      toast.success('領収金額を変更しました。');
    },
    onError: err => {
      toast.error(getApiErrorMessage(err, DEFAULT_FETCH_ERROR_MESSAGE));
    },
  });
}

/**
 * SCR-182 一覧印刷 for R2 — the `r_visits` counterpart of useContinuationListPdf.
 *
 * Errors are left to the caller: the page already opens a tab before firing and has to
 * close it again on failure.
 */
export function useR2VisitClinicInvoiceListPdf() {
  const { tenant, clinicNumber } = useClinicContext();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: Record<string, unknown>) =>
      printR2VisitClinicInvoiceListPdf(tenant ?? '', clinicNumber ?? '', payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['visitClinicInvoice'] });
    },
  });
}
